import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useTonConnectUI, useTonAddress, useTonWallet } from '@tonconnect/ui-react';
import {
  fetchHistory,
  fetchNotifyPrefs,
  saveNotifyPrefs,
  type NotifyPrefs,
} from '../lib/api.ts';
import { getTonBalance } from '../lib/chain.ts';
import { getInitData, inTelegram } from '../lib/telegram.ts';
import { setLang } from '../lib/i18n.ts';
import { currentTheme, setTheme, type Theme } from '../lib/theme.ts';
import { useConfirmWallet } from '../lib/confirm.ts';
import { shortAddress } from '../lib/collections.ts';
import { useFmt } from '../lib/format.tsx';




/**
 * Названия событий истории. Незнакомое показываем как есть, а не прячем.
 *
 * Виды — ровно те, что пишет индексер (DealEventKind): раньше половина
 * справочника называла события, которых протокол не порождает, а половина
 * настоящих оставалась без названия и выпадала в историю как есть —
 * «position_sold» посреди экрана.
 */
const EVENT_KEY: Record<string, string> = {
  nft_locked: 'history.nft_locked',
  payment: 'history.payment',
  deferral: 'history.deferral',
  position_sold: 'history.position_sold',
  position_assigned: 'history.position_assigned',
  position_listed: 'history.position_listed',
  default_claimed: 'history.default_claimed',
  cancelled: 'history.cancelled',
  owed_withdrawn: 'history.owed_withdrawn',
};

/**
 * Переключатель уведомления по типу.
 *
 * Типы разделены не для красоты: отказ от шумного не должен выключать
 * предупреждение о сгорающих деньгах. Раньше выбор был один на всё — и
 * человек, уставший от рассылки, терял важное вместе с ней.
 */
function Toggle({
  title,
  note,
  value,
  disabled,
  onChange,
}: {
  title: string;
  note: string;
  value: boolean;
  disabled: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <button
      className="row"
      disabled={disabled}
      onClick={() => onChange(!value)}
      style={{
        width: '100%',
        padding: '10px 0',
        borderTop: '1px solid var(--line)',
        background: 'transparent',
        border: 0,
        borderTopWidth: 1,
        borderTopStyle: 'solid',
        borderTopColor: 'var(--line)',
        color: 'var(--ink)',
        font: 'inherit',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        textAlign: 'left',
      }}
    >
      <span>
        <span style={{ display: 'block', fontSize: 12.5, fontWeight: 700 }}>{title}</span>
        <span className="dim" style={{ display: 'block', fontSize: 11, marginTop: 2 }}>{note}</span>
      </span>
      <span
        style={{
          flex: '0 0 auto',
          width: 40,
          height: 24,
          borderRadius: 999,
          background: value ? 'var(--lime)' : 'var(--sf2)',
          border: '1px solid var(--line)',
          position: 'relative',
          transition: 'background 0.15s ease',
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: 2,
            left: value ? 18 : 2,
            width: 18,
            height: 18,
            borderRadius: 999,
            background: value ? 'var(--lime-ink)' : 'var(--ink3)',
            transition: 'left 0.15s ease',
          }}
        />
      </span>
    </button>
  );
}

export function Profile({
  onGuide,
  onReferral,
  onAdmin,
}: {
  onGuide: () => void;
  onReferral: () => void;
  /** Задан только для админского кошелька мастера. */
  onAdmin?: (() => void) | undefined;
}) {
  const { t, i18n } = useTranslation();
  const { lang, money, dateTime } = useFmt();
  const qc = useQueryClient();
  const [tonConnectUI] = useTonConnectUI();
  const address = useTonAddress(false);
  const friendly = useTonAddress(true);
  const wallet = useTonWallet();
  const confirmWallet = useConfirmWallet(lang);
  const state = confirmWallet.state;
  const error = confirmWallet.error;
  const [theme, setThemeState] = useState<Theme>(currentTheme);

  const balance = useQuery({
    queryKey: ['balance', address],
    queryFn: () => getTonBalance(address),
    enabled: address.length > 0,
    staleTime: 30_000,
  });

  const prefs = useQuery({
    queryKey: ['notifyPrefs', address],
    queryFn: () => fetchNotifyPrefs(address),
    enabled: address.length > 0,
  });

  const history = useQuery({
    queryKey: ['history', address],
    queryFn: () => fetchHistory(address),
    enabled: address.length > 0,
  });

  const savePrefs = useMutation({
    mutationFn: (patch: Partial<NotifyPrefs>) =>
      saveNotifyPrefs({ initData: getInitData(), address, ...patch }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['notifyPrefs', address] }),
  });

  // Сам порядок подтверждения живёт в lib/confirm.ts: он нужен и здесь, и
  // на экране приглашений, где без подтверждённого кошелька нет ссылки.
  const onSubscribe = async () => {
    if (await confirmWallet.confirm()) {
      void qc.invalidateQueries({ queryKey: ['notifyPrefs', address] });
    }
  };

  const other = i18n.language === 'ru' ? 'en' : 'ru';
  const confirmed = prefs.data?.prefs != null;
  const current: NotifyPrefs = prefs.data?.prefs ?? {
    pay: true,
    grace: true,
    requests: true,
    board: false,
    holdings: true,
  };

  return (
    <div className="screen">
      {/* Баланс и кошелёк — первым блоком: перед взносом спрашивают «хватит
          ли», и ответ не должен быть за прокруткой. */}
      <div className="card col" style={{ gap: 14 }}>
        <div className="row" style={{ alignItems: 'flex-start' }}>
          <div>
            <div className="label">{t('profile.balance')}</div>
            <div
              className="mono num"
              style={{ fontSize: 32, fontWeight: 700, letterSpacing: '-0.03em', marginTop: 5 }}
            >
              {balance.data == null ? '—' : money(balance.data)}
            </div>
          </div>
          {wallet !== null && (
            <span className="chip">{wallet.device.appName.toUpperCase()}</span>
          )}
        </div>
        <div className="mono dim" style={{ fontSize: 11, overflowWrap: 'anywhere' }}>
          {friendly.length > 0 ? shortAddress(friendly, { wallet: true }) : '—'}
        </div>
      </div>

      <div className="card col">
        <div className="row" style={{ alignItems: 'baseline' }}>
          <span className="title">{t('profile.notifications')}</span>
          {confirmed && <span className="label lime">{t('profile.subscribed')}</span>}
        </div>
        <span className="text">{t('profile.what_we_send')}</span>

        <Toggle
          title={t('profile.notify_pay')}
          note={t('profile.notify_pay_note')}
          value={current.pay}
          disabled={!confirmed || savePrefs.isPending}
          onChange={(v) => savePrefs.mutate({ pay: v })}
        />
        <Toggle
          title={t('profile.notify_grace')}
          note={t('profile.notify_grace_note')}
          value={current.grace}
          disabled={!confirmed || savePrefs.isPending}
          onChange={(v) => savePrefs.mutate({ grace: v })}
        />
        <Toggle
          title={t('profile.notify_req')}
          note={t('profile.notify_req_note')}
          value={current.requests}
          disabled={!confirmed || savePrefs.isPending}
          onChange={(v) => savePrefs.mutate({ requests: v })}
        />
        {/* Видимость — не про письма, но спрашивается тем же тумблером:
            «показывать ли мои вещи тем, кто ищет, что купить в рассрочку».
            По умолчанию да — ради этого человек и подписывается, — но
            выбор остаётся за ним. Адрес кошелька наружу не уходит в любом
            случае: письмо доставляет сервер. */}
        <Toggle
          title={t('profile.show_holdings')}
          note={t('profile.show_holdings_note')}
          value={current.holdings}
          disabled={!confirmed || savePrefs.isPending}
          onChange={(v) => savePrefs.mutate({ holdings: v })}
        />

        {/* Переключателя «новые офферы в коллекциях» здесь нет намеренно.
            Он обещал письма, которых никто не отправлял: аудитория такой
            рассылки — покупатели, а мы знаем только держателей, то есть
            ровно тех, кому эта вещь уже принадлежит. Поле в базе осталось
            — вернуть переключатель дешевле, чем объяснять людям пропажу
            настройки, — но обещать его до появления самой рассылки нечестно. */}

        {!confirmed && (
          <>
            <span className="text" style={{ fontSize: 11.5 }}>{t('profile.confirm_first')}</span>
            {/* Зачем подпись — объясняем до кнопки, а не после отказа. */}
            <span className="dim" style={{ fontSize: 11 }}>{t('profile.why_proof')}</span>
            {!inTelegram && <div className="text">{t('profile.telegram_only')}</div>}
            {error !== null && <div className="danger" style={{ fontSize: 12 }}>{error}</div>}
            <button
              className="btn"
              /* Кнопка работает и без подключённого кошелька: сам порядок
                 начинается с отключения — подпись ton_proof кошелёк даёт
                 только при новом подключении. Раньше после первой попытки
                 адрес пропадал, кнопка гасла, и подтвердить было нечем. */
              disabled={!inTelegram || state === 'preparing' || state === 'signing'}
              onClick={() => void onSubscribe()}
            >
              {state === 'done'
                ? t('profile.subscribed')
                : state === 'signing' || state === 'preparing'
                  ? '…'
                  : t('profile.subscribe')}
            </button>
          </>
        )}
      </div>

      {/* История — из событий, собранных индексером из блокчейна. */}
      <div className="card col" style={{ gap: 2 }}>
        <div style={{ fontSize: 14.5, fontWeight: 800, marginBottom: 6 }}>{t('profile.history')}</div>
        {history.isLoading && <div className="skeleton" style={{ minHeight: 60 }} />}
        {history.data?.events.length === 0 && (
          <span className="text">{t('profile.history_empty')}</span>
        )}
        {history.data?.events.map((e) => {
          const key = EVENT_KEY[e.kind];
          const incoming = e.role === 'seller';
          return (
            <div
              key={`${e.dealAddress}-${e.ts}-${e.kind}`}
              className="row"
              style={{ padding: '10px 0', borderTop: '1px solid var(--line)' }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 600 }}>
                  {key === undefined ? e.kind : t(key)}
                </div>
                <div className="mono dim" style={{ fontSize: 9.5, marginTop: 2 }}>
                  {dateTime(e.ts)} · {shortAddress(e.dealAddress)}
                </div>
              </div>
              {BigInt(e.amount) > 0n && (
                <span
                  className={incoming ? 'mono num lime' : 'mono num'}
                  style={{ fontSize: 12.5, fontWeight: 700 }}
                >
                  {incoming ? '+' : '−'}{money(e.amount)}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {onAdmin !== undefined && (
        <button className="card row" onClick={onAdmin} style={{ cursor: 'pointer', textAlign: 'left', font: 'inherit', color: 'var(--ink)' }}>
          <span className="title">{t('nav.admin')}</span>
          <span className="dim">→</span>
        </button>
      )}

      {/* Приглашения стоят выше гайда намеренно: это единственная строка в
          профиле, за которой стоят деньги человека, а не справка. */}
      <button
        className="card row"
        onClick={onReferral}
        style={{ cursor: 'pointer', textAlign: 'left', font: 'inherit', color: 'var(--ink)' }}
      >
        <span className="col" style={{ gap: 2 }}>
          <span className="title">{t('profile.referral')}</span>
          <span className="dim" style={{ fontSize: 11 }}>{t('profile.referral_note')}</span>
        </span>
        <span className="dim">→</span>
      </button>

      <button className="card row" onClick={onGuide} style={{ cursor: 'pointer', textAlign: 'left', font: 'inherit', color: 'var(--ink)' }}>
        <span className="title">{t('guide.how_works')}</span>
        <span className="dim">→</span>
      </button>

      <div className="card row">
        <span className="text">{t('profile.theme')}</span>
        <div className="segmented" style={{ gridTemplateColumns: '1fr 1fr', flex: '0 0 auto' }}>
          {(['dark', 'light'] as const).map((x) => (
            <button
              key={x}
              aria-selected={theme === x}
              style={{ padding: '6px 12px', fontSize: 12 }}
              onClick={() => {
                setTheme(x);
                setThemeState(x);
              }}
            >
              {x === 'dark' ? t('profile.theme_dark') : t('profile.theme_light')}
            </button>
          ))}
        </div>
      </div>

      <div className="card row">
        <span className="text">{t('profile.language')}</span>
        <button className="btn btn-ghost btn-sm" style={{ width: 'auto' }} onClick={() => setLang(other)}>
          {other.toUpperCase()}
        </button>
      </div>

      {/* Отметка сборки — последней строкой, подписью, а не голым хэшем:
          сама по себе цифра ничего не значит, а «ver» объясняет, зачем она
          здесь. Нужна ровно для одного вопроса: «починено, но у вас старый
          бандл» или «не починено». Telegram кэширует вебвью упорнее
          браузера, и без номера это выясняется спором. */}
      <div className="row" style={{ justifyContent: 'center', paddingTop: 2 }}>
        <span className="dim mono" style={{ fontSize: 10, letterSpacing: '0.08em' }}>
          ver {String(import.meta.env['VITE_BUILD'] ?? 'dev').slice(0, 7)}
        </span>
      </div>
    </div>
  );
}
